/**
 * «Как посчитано» (docs/design/spend-2026-10-15): числа главной в два шага.
 *
 * Шаг 1 — «На счетах» минус «Отложено» = «Не отложено»: те же три плитки.
 * Шаг 2 — «Не отложено» минус платежи до прихода, взносы в цели и покупки,
 * под которые ещё не отложено, = «Можно тратить». Источник один —
 * `computeAvailable`, поэтому строки складываются ровно в числа главной.
 */
import {
  ZERO, add, formatMoney, isAfter, sub, type AvailableResult, type DeductionLine, type Money, type PlainDate,
} from '@skoka/core';
import { stableSnapshot, type AppState } from './store.js';

/** С какого числа главной открыли лист: его сумма подсвечивается. */
export type SpendFocus = 'acc' | 'res' | 'free' | 'total';

export interface SpendLine {
  key: string;
  icon: string;
  label: string;
  date?: PlainDate;
  amount: Money;
  /** Пояснение — в (i), а не строкой под названием. */
  tip?: string;
  /** Срок наступил, а оплата не отмечена: может быть, уже заплачено. */
  late?: boolean;
}

export interface SpendGroup {
  key: 'pay' | 'goal' | 'planned';
  label: string;
  amount: Money;
  lines: SpendLine[];
}

export interface SpendSteps {
  accounts: SpendLine[];
  onAccounts: Money;
  /** Счета «не для трат» — названы в пояснении к «На счетах». */
  excluded: string[];
  reserved: SpendLine[];
  reservedTotal: Money;
  free: Money;
  /** Дата прихода, если она твёрдая. */
  until: PlainDate | null;
  /** Конец окна расчёта: до него вычитаются платежи. */
  window: PlainDate;
  groups: SpendGroup[];
  available: Money;
  perDay: Money;
  days: number;
}

const SHORT = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
export const shortDay = (d: string): string => `${Number(d.slice(8, 10))} ${SHORT[Number(d.slice(5, 7)) - 1]}`;
const longDay = (d: string): string => `${Number(d.slice(8, 10))} ${MONTHS[Number(d.slice(5, 7)) - 1]}`;

const sumOf = (lines: readonly SpendLine[]): Money => add(...lines.map((l) => l.amount));
const byDate = (a: SpendLine, b: SpendLine) =>
  (a.date ?? '9999') < (b.date ?? '9999') ? -1 : (a.date ?? '9999') > (b.date ?? '9999') ? 1 : b.amount - a.amount;

export function spendSteps(state: AppState, result: AvailableResult): SpendSteps {
  const today = state.today;
  const spendable = state.accounts.filter((a) => a.isLiquid && !a.archived && a.kind !== 'credit_card');
  const ids = new Set(spendable.map((a) => a.id));

  // ── шаг 1: на счетах
  const accounts: SpendLine[] = spendable.map((a) => ({
    key: a.id, label: a.name, amount: a.balance,
    icon: a.kind === 'cash' ? 'money-bill' : a.kind === 'savings' ? 'piggy-bank' : 'credit-card',
  }));
  const excluded = state.accounts
    .filter((a) => !a.archived && !a.isLiquid && a.kind !== 'credit_card')
    .map((a) => a.name);

  // ── шаг 1: отложено — конверты на счетах для трат, по тому, подо что
  const nextPay = (id: string): PlainDate | undefined => result.upcoming
    .filter((u) => u.kind === 'obligation' && u.id === id && !u.settled)
    .map((u) => u.date).sort()[0];
  const stable = stableSnapshot(state);
  const payout = stable?.horizonPayout ?? null;
  const held = new Map<string, SpendLine>();
  for (const r of state.reserves) {
    if (!ids.has(r.accountId)) continue;
    const key = `${r.forKind}:${r.forId}`;
    const line = held.get(key) ?? reservedLine(state, result, r.forKind, r.forId, nextPay, payout?.scheduledDate);
    held.set(key, { ...line, amount: add(line.amount, r.amount) });
  }
  const order = { obligation: 0, credit: 1, planned: 2, stable: 3, goal: 4 } as const;
  const reserved = [...held.values()]
    .filter((l) => l.amount !== ZERO)
    .sort((a, b) => (order[a.key.split(':')[0] as keyof typeof order] ?? 9) - (order[b.key.split(':')[0] as keyof typeof order] ?? 9) || byDate(a, b));
  const onAccounts = sumOf(accounts);
  const reservedTotal = sumOf(reserved);
  const free = sub(onAccounts, reservedTotal);

  // ── шаг 2: что ещё вычесть до прихода
  const of = (kind: DeductionLine['kind']) => result.deductions.filter((d) => d.kind === kind);
  const pay: SpendLine[] = [
    ...of('obligation').map((d) => payLine(state, result, d, today)),
    ...of('credit').map((d) => cardLine(result, d)),
  ].sort(byDate);
  const goals: SpendLine[] = of('goal').map((d) => goalLine(state, d));
  const planned: SpendLine[] = of('planned').map((d) => {
    const p = state.plannedExpenses.find((x) => x.id === d.id);
    const kept = p ? sub(p.amount, d.amount) : ZERO;
    return {
      key: `planned:${d.id}`, icon: 'cart-shopping', label: d.label, amount: d.amount,
      ...(d.date ? { date: d.date } : {}),
      ...(p && kept > ZERO ? { tip: `Покупка ${formatMoney(p.amount)}, из них ${formatMoney(kept)} уже отложено.` } : {}),
    };
  }).sort(byDate);
  const window = result.horizonDate;
  const groups: SpendGroup[] = [
    { key: 'pay' as const, label: `Платежи до ${longDay(window)}`, lines: pay },
    { key: 'goal' as const, label: 'Взносы в цели этого месяца', lines: goals },
    { key: 'planned' as const, label: 'Запланированные покупки', lines: planned },
  ].filter((g) => g.lines.length > 0).map((g) => ({ ...g, amount: sumOf(g.lines) }));

  return {
    accounts, onAccounts, excluded, reserved, reservedTotal, free,
    until: result.horizon.basis === 'confirmed' ? result.horizonDate : null,
    window, groups,
    available: sub(free, add(...groups.map((g) => g.amount))),
    perDay: result.perDay, days: result.daysToHorizon,
  };
}

function reservedLine(
  state: AppState, result: AvailableResult, kind: string, id: string,
  nextPay: (id: string) => PlainDate | undefined, payout: PlainDate | undefined,
): SpendLine {
  const key = `${kind}:${id}`;
  if (kind === 'obligation') {
    const date = nextPay(id);
    return { key, icon: 'calendar', label: state.obligations.find((o) => o.id === id)?.name ?? 'Платёж', amount: ZERO, ...(date ? { date } : {}) };
  }
  if (kind === 'credit') {
    const date = result.credit.find((c) => c.accountId === id)?.paymentDate;
    return { key, icon: 'credit-card', label: state.accounts.find((a) => a.id === id)?.name ?? 'Кредитка', amount: ZERO, ...(date ? { date } : {}) };
  }
  if (kind === 'goal') {
    const g = state.goals.find((x) => x.id === id);
    return { key, icon: g?.kind === 'emergency_fund' ? 'umbrella' : 'bullseye-arrow', label: g?.name ?? 'Цель', amount: ZERO };
  }
  if (kind === 'planned') {
    const p = state.plannedExpenses.find((x) => x.id === id);
    return { key, icon: 'cart-shopping', label: p?.name ?? 'Покупка', amount: ZERO, ...(p ? { date: p.expectedDate } : {}) };
  }
  return {
    key, icon: 'lock', label: 'Запас стабильного бюджета', amount: ZERO,
    ...(payout ? { tip: `Выплаты себе: следующая ${longDay(payout)}.` } : {}),
  };
}

/** Платёж до прихода: что осталось отложить, и почему сумма такая. */
function payLine(state: AppState, result: AvailableResult, d: DeductionLine, today: PlainDate): SpendLine {
  const up = result.upcoming.find((u) => u.kind === 'obligation' && u.id === d.id && u.date === d.date);
  const o = state.obligations.find((x) => x.id === d.id);
  const extra = state.loans.find((l) => l.obligationId === d.id)?.extraMonthly ?? ZERO;
  const tips: string[] = [];
  if (up && extra > ZERO && extra < up.amount) {
    tips.push(`По графику ${formatMoney(sub(up.amount, extra))} + досрочно ${formatMoney(extra)}.`);
  }
  if (up && up.covered > ZERO) tips.push(`Платёж ${formatMoney(up.amount)}, из них ${formatMoney(up.covered)} уже отложено.`);
  if (o?.amountType === 'variable') tips.push('Сумма — оценка по прошлым платежам.');
  return {
    key: `pay:${d.instanceId ?? d.id}`, icon: 'calendar', label: d.label, amount: d.amount,
    ...(d.date ? { date: d.date } : {}),
    ...(tips.length ? { tip: tips.join(' ') } : {}),
    ...(d.date && !isAfter(d.date, today) ? { late: true } : {}),
  };
}

function cardLine(result: AvailableResult, d: DeductionLine): SpendLine {
  const up = result.upcoming.find((u) => u.kind === 'credit' && u.id === d.id && u.date === d.date && u.label === d.label);
  const after = d.note?.startsWith('покупки после выписки') ?? false;
  const tip = after ? 'Покупки после выписки.'
    : up && up.covered > ZERO ? `Платёж ${formatMoney(up.amount)}, из них ${formatMoney(up.covered)} уже внесено или отложено.` : '';
  return {
    key: `card:${d.id}:${d.date ?? ''}:${d.label}`, icon: 'credit-card', label: d.label, amount: d.amount,
    ...(d.date ? { date: d.date } : {}),
    ...(tip ? { tip } : {}),
  };
}

function goalLine(state: AppState, d: DeductionLine): SpendLine {
  const g = state.goals.find((x) => x.id === d.id);
  const overCard = d.note?.startsWith('сверх платежа') ?? false;
  const kept = g && !overCard ? sub(g.monthlyContribution, d.amount) : ZERO;
  const tip = overCard ? 'Сверх платежа по карте — он в платежах.'
    : g && kept > ZERO ? `Взнос ${formatMoney(g.monthlyContribution)}, из них ${formatMoney(kept)} уже отложено.` : '';
  return {
    key: `goal:${d.id}`, icon: g?.kind === 'emergency_fund' ? 'umbrella' : 'bullseye-arrow', label: d.label, amount: d.amount,
    ...(tip ? { tip } : {}),
  };
}
